import React from "react";
import { Link } from "react-router-dom";
import { Box, Card, Typography } from "@mui/material";
import { ResponsiveContainer, LineChart, Line } from "recharts";

const STATUS_COLORS = ["#2f63ad", "#b45309", "#6b7280", "#8b5cf6", "#0ea5e9"];

const formatDelta = (thisMonth, lastYear) => {
  if (!lastYear) return thisMonth > 0 ? { label: "New", color: "#2f63ad" } : null;
  const pct = Math.round(((thisMonth - lastYear) / lastYear) * 100);
  const sign = pct > 0 ? "+" : "";
  return { label: `${sign}${pct}%`, color: pct > 0 ? "#dc2626" : pct < 0 ? "#16a34a" : "#6b778c" };
};

// One "Work Orders / Violations / Architectural Requests" summary card for
// the main Home dashboard: this-month count + YoY delta + sparkline, then
// total/resolved/avg-close, then a segmented bar of the real (non-resolved)
// statuses — no invented buckets, since the mockup's category names don't
// match this schema's real status vocabulary (see project memory).
const ModuleSummaryCard = ({ title, viewAllLink, data, lastYearLabel }) => {
  if (!data) return null;

  const { total, thisMonth, sameMonthLastYear, resolved, avgCloseDays, statusBreakdown, sparkline } = data;
  const delta = formatDelta(thisMonth, sameMonthLastYear);
  const barTotal = statusBreakdown.reduce((sum, s) => sum + s.count, 0) || 1;

  return (
    <Card sx={{ borderRadius: "16px", p: 2.5, border: "1px solid rgba(0,0,0,0.06)", boxShadow: "0 4px 16px rgba(0,0,0,0.04)" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>{title}</Typography>
        <Typography component={Link} to={viewAllLink} sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#2f63ad", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
          View all
        </Typography>
      </Box>

      <Typography variant="caption" sx={{ color: "#9aa5b4", fontWeight: 700, letterSpacing: 0.5 }}>
        THIS MONTH
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 0.25, mb: 1.5 }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#223142" }}>{thisMonth}</Typography>
            {delta && (
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: delta.color }}>{delta.label}</Typography>
            )}
          </Box>
          <Typography variant="caption" sx={{ color: "#9aa5b4" }}>
            vs {sameMonthLastYear} in {lastYearLabel}
          </Typography>
        </Box>
        <Box sx={{ width: 90, height: 40 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkline.map((v, i) => ({ v, i }))}>
              <Line type="monotone" dataKey="v" stroke="#2f63ad" strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <Box sx={{ display: "flex", borderTop: "1px solid rgba(0,0,0,0.06)", pt: 1.25, mb: 1.5 }}>
        {[
          ["TOTAL", total],
          ["RESOLVED", resolved],
          ["AVG CLOSE", avgCloseDays != null ? `${avgCloseDays}d` : "—"],
        ].map(([label, value]) => (
          <Box key={label} sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: "#9aa5b4", fontWeight: 700, letterSpacing: 0.3, display: "block" }}>
              {label}
            </Typography>
            <Typography sx={{ fontWeight: 700, color: "#223142" }}>{value}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", height: 6, borderRadius: "4px", overflow: "hidden", mb: 1 }}>
        {statusBreakdown.map((s, i) => (
          <Box key={s.status} sx={{ width: `${(s.count / barTotal) * 100}%`, backgroundColor: STATUS_COLORS[i % STATUS_COLORS.length] }} />
        ))}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        {statusBreakdown.map((s, i) => (
          <Box key={s.status} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: STATUS_COLORS[i % STATUS_COLORS.length] }} />
            <Typography variant="caption" sx={{ color: "#6b778c" }}>{s.status} {s.count}</Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
};

export default ModuleSummaryCard;
